import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Upload, X, Check, ImageIcon as Img, Trash2, ExternalLink, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * MediaPicker modal.
 * - mode="picker": clicking a media item returns its full URL via onPick(url).
 * - mode="manage": library management view (no select callback).
 * - open / onClose controls visibility.
 */
export default function MediaPicker({ open, onClose, onPick, mode = "picker" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/media`);
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const fullUrl = (item) => (item.url.startsWith("http") ? item.url : `${BACKEND}${item.url}`);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("File is too large (max 6 MB).");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("alt", "");
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/media`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded!");
      setItems((prev) => [data, ...prev]);
      if (mode === "picker" && onPick) onPick(fullUrl(data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      // reset input so the same file can be re-selected
      e.target.value = "";
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/media/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const handlePickItem = (item) => {
    if (mode === "picker" && onPick) {
      onPick(fullUrl(item));
    }
  };

  const handleUseExternal = () => {
    if (!externalUrl.trim()) return;
    if (onPick) onPick(externalUrl.trim());
    setExternalUrl("");
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="media-picker-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Img className="h-4 w-4 text-sky-600" />
            <h2 className="text-sm font-bold text-slate-900">
              {mode === "picker" ? "Select an image" : "Media Library"}
            </h2>
            <span className="text-xs text-slate-400">({items.length})</span>
          </div>
          <button
            onClick={onClose}
            data-testid="media-picker-close"
            className="text-slate-400 hover:text-slate-700 transition-colors p-1"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <label
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white cursor-pointer transition-colors ${
              uploading ? "opacity-60 cursor-wait" : ""
            }`}
            data-testid="media-picker-upload-label"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? "Uploading…" : "Upload image"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="media-picker-upload-input"
            />
          </label>

          {mode === "picker" && (
            <div className="flex items-center gap-2 flex-1 sm:max-w-md">
              <Input
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                placeholder="…or paste an external image URL"
                className="h-9 text-xs"
                data-testid="media-picker-external-url"
              />
              <Button
                size="sm"
                onClick={handleUseExternal}
                disabled={!externalUrl.trim()}
                data-testid="media-picker-external-use"
                className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-9 rounded-lg px-3"
              >
                Use
              </Button>
            </div>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Img className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No images yet. Upload one to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((item) => {
                const url = fullUrl(item);
                return (
                  <div
                    key={item.id}
                    data-testid={`media-item-${item.id}`}
                    className="group relative bg-slate-50 border border-slate-200 rounded-lg overflow-hidden hover:border-sky-300 transition-colors"
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handlePickItem(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handlePickItem(item);
                      }}
                      className={`relative w-full aspect-square flex items-center justify-center bg-white ${
                        mode === "picker" ? "cursor-pointer" : ""
                      }`}
                    >
                      <img
                        src={url}
                        alt={item.alt || item.filename}
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                      />
                      {mode === "picker" && (
                        <div className="absolute inset-0 bg-sky-600/0 group-hover:bg-sky-600/15 flex items-center justify-center transition-colors">
                          <span className="opacity-0 group-hover:opacity-100 bg-white text-sky-700 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md transition-opacity inline-flex items-center gap-1">
                            <Check className="h-3 w-3" /> Use this
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="px-2 py-1.5 flex items-center gap-1 justify-between border-t border-slate-100">
                      <p className="text-[10px] text-slate-500 truncate flex-1" title={item.filename}>
                        {item.filename}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(url);
                          toast.success("URL copied");
                        }}
                        title="Copy URL"
                        className="text-slate-400 hover:text-sky-600 p-0.5"
                        data-testid={`media-copy-${item.id}`}
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open"
                        className="text-slate-400 hover:text-sky-600 p-0.5"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        title="Delete"
                        className="text-slate-400 hover:text-red-500 p-0.5"
                        data-testid={`media-delete-${item.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
